// StudioAsk — "Ask" at the one selected card (pen "Studio – click to add to
// context", §2): a dark pill at the card's top right opens a one-line prompt
// anchored under it, "About “…”", with quick actions. Sending puts the
// request in the current conversation; the card is already its context (the
// selection is the composer's mentions). Pure but for view-ephemeral state
// (what is typed); where it sits (viewport pixels) is the canvas's.

export { StudioAsk };

import { useLingui } from "@lingui/react/macro";
import {
    type KeyboardEvent,
    type ReactNode,
    type SyntheticEvent,
    useState,
} from "react";
import { Icon } from "../Icon/Icon.js";
import type { Point } from "../Studio/studioLayout.js";
import "./StudioAsk.css";

// The prompt owns its pointer and keys: a press never starts a pan or a
// drag, and the canvas's single letters never act on what is typed.
const own = (e: SyntheticEvent) => e.stopPropagation();

const StudioAsk = ({
    title,
    quiz,
    pill,
    prompt,
    open,
    online,
    onOpen,
    onClose,
    onSend,
}: {
    readonly title: string;
    // A quiz gets "Add a question" among the quick actions.
    readonly quiz: boolean;
    // The pill's top-right corner, and the prompt's top-left (viewport px).
    readonly pill: Point;
    readonly prompt: Point;
    readonly open: boolean;
    readonly online: boolean;
    readonly onOpen: () => void;
    readonly onClose: () => void;
    readonly onSend: (text: string) => void;
}): ReactNode => {
    const { t } = useLingui();
    const [text, setText] = useState("");
    const send = (what: string) => {
        if (what.trim() === "" || !online) return;
        onSend(what.trim());
        setText("");
    };
    const quick = [
        t`Simplify`,
        t`Explain`,
        ...(quiz ? [t`Add a question`] : []),
    ];
    const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
        own(e);
        if (e.key === "Escape") {
            e.preventDefault();
            onClose();
        } else if (e.key === "Enter") {
            e.preventDefault();
            send(text);
        }
    };
    if (!open)
        return (
            <button
                type="button"
                className="bilda-ask__pill"
                style={{ left: pill.x, top: pill.y }}
                disabled={!online}
                onPointerDown={own}
                onClick={onOpen}
            >
                <Icon name="sparkle" size="sm" />
                {t`Ask`}
            </button>
        );
    return (
        <div
            className="bilda-ask"
            style={{ left: prompt.x, top: prompt.y }}
            onPointerDown={own}
        >
            <p className="bilda-ask__about">
                <Icon name="sparkle" size="sm" />
                <span>{t`About “${title}”`}</span>
            </p>
            <div className="bilda-ask__field">
                <input
                    // biome-ignore lint/a11y/noAutofocus: opening Ask is asking to type.
                    autoFocus
                    value={text}
                    aria-label={t`About “${title}”`}
                    placeholder={t`Ask the assistant …`}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={onKey}
                />
                <button
                    type="button"
                    className="bilda-ask__send"
                    aria-label={t`Send`}
                    disabled={!online || text.trim() === ""}
                    onClick={() => send(text)}
                >
                    <Icon name="arrow-up" size="sm" />
                </button>
            </div>
            <div className="bilda-ask__quick">
                {quick.map((q) => (
                    <button
                        key={q}
                        type="button"
                        disabled={!online}
                        onClick={() => send(q)}
                    >
                        {q}
                    </button>
                ))}
            </div>
        </div>
    );
};
